import { ApiError } from '@lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShiftReactionStatus, ShiftStatus, TeacherShift } from 'types/collections';

import { ShiftService } from '@services/ShiftService';

export const useShift = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isError, error } = useQuery<TeacherShift[], ApiError>({
    queryKey: ['teacher-shifts'],
    queryFn: () => ShiftService.getShiftReactions(),
  });

  const createShiftReactionMutation = useMutation({
    mutationFn: ({
      shift_id,
      validationData,
    }: {
      shift_id: string;
      validationData: { teacherId: string; shiftStatus: ShiftStatus };
    }) => ShiftService.createShiftReaction(shift_id, validationData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher-shifts'] });
    },
  });

  const submitShiftReactionMutation = useMutation({
    mutationFn: ({
      shiftId,
      reaction,
      substituteName,
    }: {
      shiftId: string;
      reaction: ShiftReactionStatus.APPLIED | ShiftReactionStatus.REFUSED;
      substituteName?: string;
    }) => ShiftService.submitShiftReaction(shiftId, reaction, substituteName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher-shifts'] });
    },
  });

  return {
    shiftReactions: data,
    isPending,
    isError,
    error,
    createShiftReaction: createShiftReactionMutation.mutate,
    submitShiftReaction: submitShiftReactionMutation.mutate,
    isSubmittingReaction: submitShiftReactionMutation.isPending,
  };
};
